// src/hooks/useLearningJourney.js
//
// Central controller for the Learning Journey flow. Holds the UI state of all steps,
// hydrates it from the server session, autosaves drafts (useSessionSync), confirms
// steps and runs the tool's AI (steps 5 and 7). The server enforces the step rules;
// this hook mirrors them so the UI can explain what is needed next.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { confirmStep, getCatalog, getSession, getStep, runStep, saveStep } from "../api";
import { canonical, useSessionSync } from "./useSessionSync";
import { createFillDummyValues, createFillDummyValuesCertification } from "../utils/fillDummyValues";
import { loadMLUMapping, mapMLUCodesToProductIDs } from "../utils/addMLUsToWooCommerceCart";
import { planToMarkdown } from "../utils/teachingPlan";
import { friendlyError } from "../utils/errors";

const DEFAULT_LDT = "The learner is not connected to Local Digital Twin projects.";
const FRAMEWORKS = ["education", "governance", "technical", "ethics"];
const emptySelection = () => ({ education: [], governance: [], technical: [], ethics: [] });

/** Server step data -> the exact shape the UI builds (so unchanged data is never re-saved). */
export function normalizeStep(step, d = {}) {
  d = d || {};
  switch (step) {
    case 1:
      return { org_goal: d.org_goal ?? "", certification_needed: !!d.certification_needed };
    case 2:
      return {
        is_group_mode: !!d.is_group_mode,
        job_title: d.job_title ?? "",
        job_experience: d.job_experience ?? "",
        education_background: d.education_background ?? "",
        ldt_connection: d.ldt_connection || DEFAULT_LDT,
        learning_motivation: d.learning_motivation ?? "",
      };
    case 3:
      return { future_roles: d.future_roles ?? [], expected_contribution: d.expected_contribution ?? "" };
    case 4:
      return Object.fromEntries(FRAMEWORKS.map((k) => [k, d[k] ?? []]));
    case 6:
      return {
        selection: { ...emptySelection(), ...(d.selection || {}) },
        notes_by_framework: d.notes_by_framework ?? {},
        alignment_notes: d.alignment_notes ?? "",
      };
    case 7:
      return { intro: d.intro ?? "", outro: d.outro ?? "", rows: d.rows ?? [], markdown: d.markdown ?? "" };
    default:
      return d;
  }
}

const isDone = (step, view) => {
  if (!view) return false;
  if (step === 5) return view.status === "proposed";
  return view.status === "confirmed";
};

export function useLearningJourney({ sessionReady, notify } = {}) {
  const [activeStep, setActiveStep] = useState(0);
  const [snack, setSnack] = useState(null);
  const [catalogByFramework, setCatalogByFramework] = useState(null);

  // Server-side state
  const [summary, setSummary] = useState(null);
  const [stepViews, setStepViews] = useState({}); // step -> {status, version, field_sources, history, ...}
  const [hydrated, setHydrated] = useState(false);
  const [mirrorKey, setMirrorKey] = useState(null);
  const rawServer = useRef({}); // step -> data exactly as stored on the server

  // Step 0
  const [step0Completed, setStep0Completed] = useState(false);
  // Step 1
  const [orgGoal, setOrgGoal] = useState("");
  const [certificationNeeded, setCertificationNeeded] = useState(false);
  // Step 2
  const [profile, setProfile] = useState(normalizeStep(2));
  // Step 3
  const [futureRoles, setFutureRoles] = useState([]);
  const [expectedContribution, setExpectedContribution] = useState("");
  const [customRoleInput, setCustomRoleInput] = useState("");
  // Step 4
  const [selEdu, setSelEdu] = useState([]);
  const [selGov, setSelGov] = useState([]);
  const [selTech, setSelTech] = useState([]);
  const [selEth, setSelEth] = useState([]);
  // Step 5
  const [futureSelection, setFutureSelection] = useState(null);
  const [shortlistPreview, setShortlistPreview] = useState([]);
  const [step5Meta, setStep5Meta] = useState({});
  const [step5Loading, setStep5Loading] = useState(false);
  const [step5Error, setStep5Error] = useState(null);
  const [showRawStep5, setShowRawStep5] = useState(false);
  // Step 6
  const [alignmentNotes, setAlignmentNotes] = useState("");
  const [notesByFramework, setNotesByFramework] = useState(null);
  const [step6HasSelection, setStep6HasSelection] = useState(false);
  const [futureEdu, setFutureEdu] = useState([]);
  const [futureGov, setFutureGov] = useState([]);
  const [futureTech, setFutureTech] = useState([]);
  const [futureEth, setFutureEth] = useState([]);
  // Step 7
  const [plan, setPlan] = useState(normalizeStep(7));
  const [step7Loading, setStep7Loading] = useState(false);
  const [step7Error, setStep7Error] = useState(null);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [cartAddCount, setCartAddCount] = useState(0);
  const [cartTotalCount, setCartTotalCount] = useState(0);
  const [hasDownloadedJourney, setHasDownloadedJourney] = useState(false);
  const [showDownloadWarning, setShowDownloadWarning] = useState(false);

  // LLM gating cards for steps 5 and 7
  const [hasAcknowledgedStep5, setHasAcknowledgedStep5] = useState(false);
  const [hasAcknowledgedStep7, setHasAcknowledgedStep7] = useState(false);
  const [botCheckPending, setBotCheckPending] = useState(null); // {step, force}
  const [sampleActive, setSampleActive] = useState(false);

  const [stepValidity, setStepValidity] = useState({ 0: true, 1: false, 2: false, 3: true, 4: true, 5: true, 6: true, 7: true });
  const updateStepValidity = useCallback((stepNumber, isValid) => {
    setStepValidity((prev) => (prev[stepNumber] === isValid ? prev : { ...prev, [stepNumber]: isValid }));
  }, []);

  const say = useCallback((type, text) => {
    setSnack({ type, text });
    notify?.(type, text);
  }, [notify]);

  // ------------------------------------------------------------------
  // Local state <-> step data
  // ------------------------------------------------------------------
  const localData = useMemo(() => ({
    1: { org_goal: orgGoal, certification_needed: !!certificationNeeded },
    2: normalizeStep(2, profile),
    3: { future_roles: futureRoles, expected_contribution: expectedContribution },
    4: { education: selEdu, governance: selGov, technical: selTech, ethics: selEth },
    6: {
      selection: { education: futureEdu, governance: futureGov, technical: futureTech, ethics: futureEth },
      notes_by_framework: notesByFramework || {},
      alignment_notes: alignmentNotes || "",
    },
    7: normalizeStep(7, plan),
  }), [orgGoal, certificationNeeded, profile, futureRoles, expectedContribution, selEdu, selGov, selTech, selEth,
    futureEdu, futureGov, futureTech, futureEth, notesByFramework, alignmentNotes, plan]);

  const applyStep = useCallback((step, data) => {
    const d = normalizeStep(step, data);
    switch (step) {
      case 1:
        setOrgGoal(d.org_goal);
        setCertificationNeeded(d.certification_needed);
        break;
      case 2:
        setProfile(d);
        break;
      case 3:
        setFutureRoles(d.future_roles);
        setExpectedContribution(d.expected_contribution);
        break;
      case 4:
        setSelEdu(d.education);
        setSelGov(d.governance);
        setSelTech(d.technical);
        setSelEth(d.ethics);
        break;
      case 5:
        setFutureSelection(data?.future || null);
        setShortlistPreview(data?.shortlist_preview || []);
        setStep5Meta({ fallback: !!data?.fallback, cached: !!data?.cached, generated_at: data?.generated_at });
        break;
      case 6:
        setFutureEdu(d.selection.education);
        setFutureGov(d.selection.governance);
        setFutureTech(d.selection.technical);
        setFutureEth(d.selection.ethics);
        setNotesByFramework(Object.keys(d.notes_by_framework).length ? d.notes_by_framework : null);
        setAlignmentNotes(d.alignment_notes);
        break;
      case 7:
        setPlan(d);
        break;
      default:
    }
  }, []);

  const setView = useCallback((step, view) => {
    setStepViews((prev) => ({ ...prev, [step]: view }));
  }, []);

  const markStale = useCallback((steps) => {
    if (!steps || steps.length === 0) return;
    setStepViews((prev) => {
      const next = { ...prev };
      steps.forEach((n) => {
        if (next[n]) next[n] = { ...next[n], status: "stale" };
      });
      return next;
    });
    if (steps.includes(5)) setHasAcknowledgedStep5(false);
    if (steps.includes(7)) setHasAcknowledgedStep7(false);
  }, []);

  const sync = useSessionSync({
    mirrorKey,
    localData,
    onSaved: (step, view) => {
      rawServer.current[step] = view.data;
      setView(step, view);
      if (view.staled_steps?.length) {
        markStale(view.staled_steps);
        say("info", `Your change affects later steps (${view.staled_steps.join(", ")}). They need to be redone.`);
      }
    },
    onConflict: (step, current) => {
      rawServer.current[step] = current.data;
      applyStep(step, current.data);
      setView(step, current);
      say("warning", `Step ${step} was changed elsewhere (for example by your assistant). The latest version was loaded.`);
    },
    onError: (step, err) => say("error", friendlyError(err, `Step ${step} could not be saved.`)),
  });

  const refreshSummary = useCallback(async () => {
    const s = await getSession();
    setSummary(s);
    return s;
  }, []);

  // ------------------------------------------------------------------
  // Hydration from the server session
  // ------------------------------------------------------------------
  const hydrate = useCallback(async () => {
    const s = await getSession();
    const views = await Promise.all([1, 2, 3, 4, 5, 6, 7].map((n) => getStep(n)));
    const byStep = {};
    views.forEach((v) => {
      byStep[v.step] = v;
      rawServer.current[v.step] = v.data;
      applyStep(v.step, v.data);
      if (v.step !== 5) sync.markSynced(v.step, normalizeStep(v.step, v.data), v.version);
    });
    setStepViews(byStep);
    setSummary(s);
    const key = s.session.created_at;
    setMirrorKey(key);
    setHasAcknowledgedStep5(isDone(5, byStep[5]));
    setHasAcknowledgedStep7((byStep[7]?.data?.rows || []).length > 0 && byStep[7].status !== "stale");
    setHydrated(true);
    return { summary: s, views: byStep, mirrorKey: key };
  }, [applyStep, sync]);

  useEffect(() => {
    if (!sessionReady) return;
    let cancelled = false;
    (async () => {
      try {
        const { views } = await hydrate();
        if (cancelled) return;
        // Restore drafts typed offline before a reload (only if the server did not move on).
        const mirror = sync.readUnsyncedMirror();
        const restored = Object.entries(mirror).filter(([n, m]) => views[n] && m.version === views[n].version);
        restored.forEach(([n, m]) => applyStep(Number(n), m.data));
        if (restored.length) say("info", "We restored changes that had not been saved yet.");
      } catch (err) {
        say("error", friendlyError(err, "Could not load your session."));
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionReady]);

  // Pick up proposals from an AI assistant while the tool is open (flow B).
  const pullServerChanges = useCallback(async () => {
    if (!hydrated) return;
    try {
      const s = await refreshSummary();
      const dirty = new Set(sync.dirtySteps());
      for (const n of [1, 2, 3, 4]) {
        const remote = s.steps[String(n)];
        const local = stepViews[n];
        if (remote && local && remote.version > (local.version || 0) && !dirty.has(n)) {
          const v = await getStep(n);
          rawServer.current[n] = v.data;
          applyStep(n, v.data);
          sync.markSynced(n, normalizeStep(n, v.data), v.version);
          setView(n, v);
          if (v.source === "assistant") say("info", `Your assistant proposed new input for step ${n}. Please review it.`);
        }
      }
      ["5", "6", "7"].forEach((n) => {
        if (s.steps[n]?.status === "stale" && stepViews[n]?.status !== "stale") markStale([Number(n)]);
      });
    } catch {
      /* offline: next poll will retry */
    }
  }, [hydrated, refreshSummary, sync, stepViews, applyStep, setView, say, markStale]);

  useEffect(() => {
    if (!hydrated) return undefined;
    const onFocus = () => pullServerChanges();
    window.addEventListener("focus", onFocus);
    const poll = summary?.assistant_codes_active > 0 ? setInterval(pullServerChanges, 10000) : null;
    return () => {
      window.removeEventListener("focus", onFocus);
      if (poll) clearInterval(poll);
    };
  }, [hydrated, summary?.assistant_codes_active, pullServerChanges]);

  // Flush pending edits when the user changes step.
  useEffect(() => {
    sync.flush();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStep]);

  // ------------------------------------------------------------------
  // Catalogue (for exports and selectors)
  // ------------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;
    getCatalog()
      .then((res) => {
        const grouped = { education: [], ethics: [], governance: [], technical: [] };
        (res?.items || []).forEach((item) => {
          const fw = String(item.framework || "").toLowerCase();
          const key = fw.startsWith("edu") ? "education" : fw.includes("ethic") ? "ethics"
            : fw.startsWith("gov") ? "governance" : fw.startsWith("tec") ? "technical" : null;
          if (key) grouped[key].push({ code: item.id || "", title: item.title || "", learningOutcome: item.outcome || "" });
        });
        if (!cancelled) setCatalogByFramework(grouped);
      })
      .catch((err) => console.error("Failed to load catalog:", err));
    return () => {
      cancelled = true;
    };
  }, []);

  // ------------------------------------------------------------------
  // Confirm (the only way forward for steps 1-4, 6 and 7)
  // ------------------------------------------------------------------
  const handleConfirm = useCallback(async (step) => {
    await sync.idle();
    let data = localData[step];
    if (step === 3) {
      const v = customRoleInput.trim();
      if (v && !futureRoles.includes(v)) {
        const roles = [...futureRoles, v];
        setFutureRoles(roles);
        setCustomRoleInput("");
        data = { ...data, future_roles: roles };
      }
    }
    try {
      let version = sync.versions.current[step] ?? stepViews[step]?.version ?? 0;
      if (sync.isDirty(step) || canonical(data) !== canonical(localData[step]) || !stepViews[step] || stepViews[step].status === "empty") {
        const saved = await saveStep(step, data, version);
        rawServer.current[step] = saved.data;
        version = saved.version;
        sync.markSynced(step, data, version);
        if (saved.staled_steps?.length) markStale(saved.staled_steps);
      }
      const res = await confirmStep(step, version);
      setView(step, res.step);
      setSummary(res.summary);
      setSampleActive(false);
      if (step === 4 && FRAMEWORKS.every((k) => (data[k] || []).length === 0)) {
        say("warning", "Step 4 confirmed with no MLUs. The suggestions are better when you tell us what the learner already knows.");
      } else {
        say("success", step === 7 ? "Teaching plan confirmed." : `Step ${step} confirmed.`);
      }
      return true;
    } catch (err) {
      if (err.code === "version_conflict" && err.current) {
        rawServer.current[step] = err.current.data;
        applyStep(step, err.current.data);
        sync.markSynced(step, normalizeStep(step, err.current.data), err.current.version);
        setView(step, err.current);
      }
      say("error", friendlyError(err, `Step ${step} could not be confirmed.`));
      return false;
    }
  }, [localData, customRoleInput, futureRoles, sync, stepViews, setView, markStale, applyStep, say]);

  // ------------------------------------------------------------------
  // The tool's own AI (steps 5 and 7)
  // ------------------------------------------------------------------
  const runAi = useCallback(async (step, { force = false } = {}) => {
    if (summary?.bot_check_required) {
      setBotCheckPending({ step, force });
      return;
    }
    const setLoading = step === 5 ? setStep5Loading : setStep7Loading;
    const setError = step === 5 ? setStep5Error : setStep7Error;
    setLoading(true);
    setError(null);
    try {
      await sync.flush();
      const res = await runStep(step, force);
      const view = res.step;
      rawServer.current[step] = view.data;
      setView(step, view);
      setSummary(res.summary);
      applyStep(step, view.data);
      if (step === 5) {
        // A new AI proposal: start the step 6 curation from it again (notes are kept).
        const f = view.data.future || emptySelection();
        setFutureEdu(f.education || []);
        setFutureGov(f.governance || []);
        setFutureTech(f.technical || []);
        setFutureEth(f.ethics || []);
        if (view.staled_steps?.length) markStale(view.staled_steps.filter((n) => n !== 5));
        say("success", view.data.fallback
          ? "The AI was unavailable, so a simple rule-based selection is shown. You can try again later."
          : res.cached ? "Suggestions loaded (same inputs as before, so no generation was used)." : "Suggestions ready.");
      } else {
        sync.markSynced(7, normalizeStep(7, view.data), view.version);
        say("success", res.cached ? "Teaching plan loaded (no generation used)." : "Teaching plan generated. You can edit it below.");
      }
    } catch (err) {
      if (err.code === "human_check_required") {
        setBotCheckPending({ step, force });
      } else {
        setError({ message: friendlyError(err, "The AI could not generate a result."), code: err.code });
      }
      if (err.code === "session_limit" || err.code === "daily_cap") refreshSummary().catch(() => {});
    } finally {
      setLoading(false);
    }
  }, [summary, sync, setView, applyStep, markStale, say, refreshSummary]);

  const handleAcknowledgeStep5 = useCallback(() => {
    setHasAcknowledgedStep5(true);
    if (!isDone(5, stepViews[5])) runAi(5);
  }, [stepViews, runAi]);

  const handleAcknowledgeStep7 = useCallback(() => {
    setHasAcknowledgedStep7(true);
    const v = stepViews[7];
    if (!v || v.status === "stale" || !(plan.rows || []).length) runAi(7);
  }, [stepViews, plan, runAi]);

  const handleRetryStep5 = useCallback(() => runAi(5, { force: isDone(5, stepViews[5]) }), [runAi, stepViews]);
  const handleRetryStep7 = useCallback(() => runAi(7, { force: (plan.rows || []).length > 0 && stepViews[7]?.status !== "stale" }), [runAi, plan, stepViews]);

  // When entering step 6 without a curation yet (or after new suggestions), start from the AI proposal.
  useEffect(() => {
    if (activeStep !== 6 || !futureSelection) return;
    const v6 = stepViews[6];
    const hasCuration = FRAMEWORKS.some((k) => (localData[6].selection[k] || []).length > 0);
    if ((!v6 || v6.status === "empty") && !hasCuration) {
      setFutureEdu(futureSelection.education || []);
      setFutureGov(futureSelection.governance || []);
      setFutureTech(futureSelection.technical || []);
      setFutureEth(futureSelection.ethics || []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStep, futureSelection]);

  const handleStep6Preview = useCallback(() => {
    const f = futureSelection || emptySelection();
    setFutureEdu(f.education || []);
    setFutureGov(f.governance || []);
    setFutureTech(f.technical || []);
    setFutureEth(f.ethics || []);
    say("success", "The AI suggestions from step 5 were reloaded.");
  }, [futureSelection, say]);

  const preview = useMemo(() => (futureSelection ? {
    recap: { future: futureSelection, alignment_notes: alignmentNotes, org_goal: orgGoal },
    preview_top: [],
  } : null), [futureSelection, alignmentNotes, orgGoal]);

  // ------------------------------------------------------------------
  // Derived data
  // ------------------------------------------------------------------
  const finalMd = useMemo(() => planToMarkdown(plan), [plan]);

  const journeySummary = {
    orgGoals: orgGoal,
    certificationNeeded,
    learnerProfile: [
      profile.job_title && `Job title: ${profile.job_title}`,
      profile.job_experience && `Experience: ${profile.job_experience}`,
      profile.education_background && `Background: ${profile.education_background}`,
      profile.ldt_connection && `LDT connection: ${profile.ldt_connection}`,
      profile.learning_motivation && `Learning motivation: ${profile.learning_motivation}`,
    ].filter(Boolean).join("\n") || "(no learner profile filled in)",
    futureRoles,
    currentKnowledge: { education: selEdu, governance: selGov, technical: selTech, ethics: selEth },
    suggestedMlus: futureSelection ? { ...emptySelection(), ...futureSelection } : undefined,
    suggestionDetails: (shortlistPreview || []).flatMap((b) => (b && Array.isArray(b.mlus) ? b.mlus : b ? [b] : [])),
    customisationNotes: notesByFramework || alignmentNotes,
    step6SelectedMlus: { education: futureEdu, governance: futureGov, technical: futureTech, ethics: futureEth },
    feedback: alignmentNotes,
    catalogByFramework,
  };

  const assistantFields = useCallback((step) => {
    const v = stepViews[step];
    if (!v || v.status === "confirmed") return [];
    return Object.entries(v.field_sources || {}).filter(([, s]) => s === "assistant").map(([f]) => f);
  }, [stepViews]);

  // ------------------------------------------------------------------
  // Sample city (pre-fills steps 1-4 as drafts; the user still confirms each step)
  // ------------------------------------------------------------------
  const dummySetters = {
    setOrgGoal, setCertificationNeeded, setProfile, setFutureRoles, setExpectedContribution,
    setSelEdu, setSelGov, setSelTech, setSelEth, setAlignmentNotes, setNotesByFramework,
  };
  const fillDummyValues = createFillDummyValues(dummySetters);
  const fillDummyValuesCertification = createFillDummyValuesCertification(dummySetters);
  const fillSampleCity = useCallback(() => {
    fillDummyValues();
    setSampleActive(true);
    say("info", "Sample data filled in for steps 1-4. Review it, replace it with your own, and confirm each step.");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [say]);

  // ------------------------------------------------------------------
  // Final action: open the T4R shop with the curated MLUs
  // ------------------------------------------------------------------
  const handleStep7Next = async () => {
    if (!hasDownloadedJourney) {
      setShowDownloadWarning(true);
      return;
    }
    setIsAddingToCart(true);
    setCartAddCount(0);
    setCartTotalCount(0);
    try {
      const selectedMLUs = FRAMEWORKS.flatMap((k) => journeySummary.step6SelectedMlus[k] || []);
      setCartTotalCount(selectedMLUs.length);
      const mappingDict = await loadMLUMapping();
      const ids = mapMLUCodesToProductIDs(selectedMLUs, mappingDict);
      setCartAddCount(ids.length);
      window.open(ids.length > 0
        ? `https://twin4resilience.k8.design/?t4r_add_products=${ids.join(",")}`
        : "https://twin4resilience.k8.design/cart/", "_blank");
    } catch (err) {
      console.error("Failed to populate cart", err);
      window.open("https://twin4resilience.k8.design/cart/", "_blank");
    } finally {
      setIsAddingToCart(false);
    }
  };

  // ------------------------------------------------------------------
  // Navigation
  // ------------------------------------------------------------------
  const status = (n) => stepViews[n]?.status || "empty";
  const isConfirmed = (n) => status(n) === "confirmed";
  const isDirty = (n) => sync.isDirty(n);

  const isNextDisabled = (() => {
    switch (activeStep) {
      case 0: return !step0Completed;
      case 1: case 2: case 3: case 4: case 6:
        return !isConfirmed(activeStep) || isDirty(activeStep);
      case 5: return !isDone(5, stepViews[5]) || !hasAcknowledgedStep5 || step5Loading;
      case 7: return !hasAcknowledgedStep7 || !(plan.rows || []).length || step7Loading || isAddingToCart;
      default: return true;
    }
  })();

  let saveHandler = null;
  let saveLabel = null;
  // Step 7 has no confirm button: the plan autosaves, and downloading it is the last action.
  if ([1, 2, 3, 4, 6].includes(activeStep)) {
    const done = isConfirmed(activeStep) && !isDirty(activeStep);
    saveLabel = done ? `Step ${activeStep} confirmed ✓` : `Confirm step ${activeStep}`;
    saveHandler = () => handleConfirm(activeStep);
  }

  const isSaveDisabled = (() => {
    if (!hydrated) return true;
    if (isConfirmed(activeStep) && !isDirty(activeStep)) return true;
    switch (activeStep) {
      case 1: return !stepValidity[1];
      case 2: return !stepValidity[2];
      case 3: return futureRoles.length === 0 && !customRoleInput.trim();
      case 4: return false;
      case 6: return !step6HasSelection;
      case 7: return step7Loading;
      default: return true;
    }
  })();

  return {
    activeStep, setActiveStep, snack, setSnack,
    summary, setSummary, refreshSummary, stepViews, hydrated, saveState: sync.saveState, flush: sync.flush,
    step0Completed, setStep0Completed,
    orgGoal, setOrgGoal, certificationNeeded, setCertificationNeeded,
    profile, setProfile,
    futureRoles, setFutureRoles, expectedContribution, setExpectedContribution, customRoleInput, setCustomRoleInput,
    selEdu, setSelEdu, selGov, setSelGov, selTech, setSelTech, selEth, setSelEth,
    futureSelection, shortlistPreview, step5Meta, step5Loading, step5Error, showRawStep5, setShowRawStep5,
    preview, alignmentNotes, setAlignmentNotes, notesByFramework, setNotesByFramework,
    futureEdu, setFutureEdu, futureGov, setFutureGov, futureTech, setFutureTech, futureEth, setFutureEth,
    step6HasSelection, setStep6HasSelection,
    plan, setPlan, finalMd, step7Loading, step7Error,
    hasAcknowledgedStep5, hasAcknowledgedStep7, hasDownloadedJourney, showDownloadWarning,
    setHasDownloadedJourney, setShowDownloadWarning,
    botCheckPending, setBotCheckPending,
    journeySummary, stepValidity, updateStepValidity, assistantFields, sampleActive,
    handleConfirm, runAi, handleStep6Preview, handleClearPreview: handleStep6Preview,
    handleRetryStep5, handleRetryStep7, handleStep7Next, handleAcknowledgeStep5, handleAcknowledgeStep7,
    markJourneyAsDownloaded: () => { setHasDownloadedJourney(true); sync.flush(); },
    applyStep, setView, hydrate,
    fillDummyValues, fillDummyValuesCertification, fillSampleCity,
    saveHandler, saveLabel, isSaveDisabled, isNextDisabled,
    isAddingToCart, cartAddCount, cartTotalCount,
  };
}
